// Aflæsning af igangværende hændelser og power-ups. Kun opslag i state, ingen sideeffekter, og kun
// typer som afhængighed, så byer, net og flow kan spørge uden cirkulære imports.

import type { GridNode, State, Threat } from './types';

/** Kører effekten fra en hændelse lige nu (efter varslet, før figuren forlader kortet)? */
function live(state: State, t: Threat): boolean {
  return t.phase === 'active' && !t.shooed && state.clockTick < t.endAt;
}

/** Den stærkeste hedebølge i gang lige nu (0 = ingen). Styrke 1 er den milde (Lærling og gæster). */
export function heatStrength(state: State): number {
  let s = 0;
  for (const t of state.threats) if (t.kind === 'heatwave' && live(state, t)) s = Math.max(s, t.strength);
  return s;
}

/** Hvor meget vind og sol der er tilbage i promille under en hedebølge af en given styrke (0 = ingen). */
export function heatKeepPm(strength: number): { wind: number; sea: number; sun: number } {
  if (strength <= 0) return { wind: 1000, sea: 1000, sun: 1000 };
  // Spor "sværhed": 60 % vind på land og hav gjorde en hedebølge i finalen til et lotteri på
  // vindbanerne. Nu står det på kortet, og havvinden holder bedre.
  return strength === 1 ? { wind: 850, sea: 950, sun: 950 } : { wind: 700, sea: 850, sun: 850 };
}

/** Hedebølge i gang, hvor ingen har planlagt køling: byerne bruger 10 % mere. */
export function heatDemand(state: State): boolean {
  for (const t of state.threats) if (t.kind === 'heatwave' && live(state, t) && t.card?.answer !== 'plan') return true;
  return false;
}

/** Ekstra forbrug i procent fra spidsbølger, der rammer byen nu. */
export function spikePct(state: State, nodeId: string): number {
  let pct = 0;
  for (const t of state.threats) {
    if (t.kind !== 'spike' || !live(state, t) || !t.nodes?.includes(nodeId)) continue;
    const p = t.pct ?? 0;
    // Bølger lægges ikke oven i hinanden: en elbil lader kun én gang ad gangen. Den største tæller.
    pct = Math.max(pct, t.card?.answer === 'smart' ? Math.round(p / 2) : p);
  }
  return pct;
}

/** Står ledningen stille, fordi borgermødet om en naboprotest kører? */
export function heldLine(state: State, lineId: string): boolean {
  for (const t of state.threats) {
    if (t.kind === 'protest' && live(state, t) && t.card?.answer === 'meeting' && t.targetLine === lineId) return true;
  }
  return false;
}

/** Sidder en småt-gæst på ledningen lige nu, så den kun bærer det halve? */
export function smaaHalved(state: State, lineId: string): boolean {
  for (const t of state.threats) if (t.kind === 'smaa' && live(state, t) && t.targetLine === lineId) return true;
  return false;
}

// --- Power-ups ------------------------------------------------------------------------------------

/** Lynreserve leverer op til 500 MW direkte hos forbrugerne. */
export const FCR_MW = 500;
/** Ring til Norge dækker op til 1.000 MW ved landingsnoden. */
export const NORWAY_MW = 1000;
/** Skagerrak lander ved Tjele. */
export const NORWAY_NODE = 'tjele';

/** Lynreserve: 500 MW hurtig reserve i 10 s. */
export function fcrActive(state: State): boolean {
  return state.clockTick < state.power.fcr.until;
}

/** Ring til Norge: landingsnoden, hvis forbindelsen er åben lige nu, ellers ''. */
export function norwayNode(state: State): string {
  return state.clockTick < state.power.norway.until ? state.power.norway.target : '';
}

/** Nødaflastning: er netop denne by kontrolleret slukket lige nu? */
export function shedNow(state: State, n: GridNode): boolean {
  return state.clockTick < state.power.shed.until && state.power.shed.target === n.id;
}

// --- Bane 5 og 6: vindstille, kabelskade og værksnedbrud --------------------------------------------

/** Den stærkeste vindstille i gang lige nu (0 = ingen). */
export function dunkelStrength(state: State): number {
  let s = 0;
  for (const t of state.threats) if (t.kind === 'dunkelflaute' && live(state, t)) s = Math.max(s, t.strength);
  return s;
}

/**
 * Hvor meget vind og sol der er tilbage i promille under en vindstille (mørkt og vindstille, en
 * "dunkelflaute"). Den milde tager godt halvdelen af vinden, de stærkere tre fjerdedele. Det er
 * spillets tal, ikke målte: rigtige dunkelflauter varer dage, her 20 sekunder.
 */
export function dunkelKeepPm(strength: number): { wind: number; sea: number; sun: number } {
  if (strength <= 0) return { wind: 1000, sea: 1000, sun: 1000 };
  return strength === 1 ? { wind: 450, sea: 500, sun: 500 } : { wind: 250, sea: 300, sun: 350 };
}

/** Bærer den faste forbindelse kun det halve lige nu, fordi en kabelskade er i gang? */
export function cableHalved(state: State, lineId: string): boolean {
  for (const t of state.threats) if (t.kind === 'cable_half' && live(state, t) && t.targetLine === lineId) return true;
  return false;
}

/** Er værket faldet ud lige nu (værksnedbrud)? */
export function trippedPlant(state: State, nodeId: string): boolean {
  for (const t of state.threats) if (t.kind === 'plant_trip' && live(state, t) && t.nodes?.[0] === nodeId) return true;
  return false;
}

/** Har nettet selv koblet byen fra, fordi frekvensen faldt under 49,0 Hz (UFLS)? */
export function uflsShed(state: State, nodeId: string): boolean {
  for (const t of state.threats) {
    if (t.kind === 'plant_trip' && t.shedNode === nodeId && state.clockTick < (t.shedUntil ?? 0)) return true;
  }
  return false;
}

/** Frekvensen kravler tilbage til 50,00 Hz på 15 s efter et dyk. */
export const FREQ_RECOVER_TICKS = 150;

/** Frekvensen i prisområdet i centihertz (5000 = 50,00 Hz). Kun værksnedbrud flytter den i spillet. */
export function freqCHz(state: State, area: 'DK1' | 'DK2'): number {
  let f = 5000;
  for (const t of state.threats) {
    if (t.kind !== 'plant_trip' || t.area !== area || t.nadir === undefined || t.phase !== 'active') continue;
    const since = state.clockTick - t.hitAt;
    if (since < 0 || since >= FREQ_RECOVER_TICKS) continue;
    f = Math.min(f, Math.round(t.nadir + ((5000 - t.nadir) * since) / FREQ_RECOVER_TICKS));
  }
  return f;
}
